/**
 * ONE rendered list of an email's stored documents, wherever it is read — the expanded timeline
 * row and the full message view / modal
 * (`docs/specs/strategic-architecture/governed-email-content-retrieval-spec.md` §3.1, §3.2).
 *
 * PRESENTATIONAL ONLY, and — exactly as with `InlineMessageBody` — that is the governance point
 * rather than a style preference: each surface keeps its OWN fetch through its OWN governed,
 * audited door, and shares only the rendering. Nothing here fetches and nothing here decides who
 * may read a file. The parent hands it a status envelope and two callbacks.
 *
 * The awkward states are the reason this is shared at all. A second, hastily-written list is a list
 * that drops them:
 *
 *   * **loading** shows NO filenames. A filename is content ("Project Falcon CIM.pdf" is itself the
 *     disclosure), so nothing from a previous open may paint while this open is still being
 *     authorized (spec §3.2 "Checking permissions").
 *   * **blocked** keeps the filename and removes every action — "Removed by security scanning",
 *     with no retry and no description of the threat (owner Ruling 1, spec §4.2).
 *   * **scan_pending** is not an error and not a release: "Checking this file…", no download.
 *   * **not_stored** (unsupported type, oversize) keeps the name VISIBLE with the actions disabled,
 *     because an entitled reader is allowed to know the file was there.
 *   * **empty** is the authoritative negative and says so in words; it is NOT the same as
 *     **not_captured**, which means nobody has looked yet.
 *
 * `data-attachment-list` / `data-attachment-state` are the tested contract (CSS-module class names
 * are hashed), the same discipline `InlineMessageBody` and `EvidenceBody` use.
 */
import type { CrmEmailAttachmentItem, CrmEmailContent } from '../../types';
import styles from './AttachmentList.module.css';
import { formatSize, typeLabel, type AttachmentListState } from './attachmentDisplay';

/** The words, in one place, so the row and the message view cannot tell different stories. */
export const ATTACHMENT_COPY = {
  loading: 'Checking permissions…',
  empty: 'No document attachments',
  notCaptured: 'Attachments not yet captured',
  partial: 'Attachment list is incomplete',
  blocked: 'Removed by security scanning',
  scanPending: 'Checking this file…',
  notStored: 'Not available',
  unavailable: 'Not available',
  error: 'Failed to load attachments.',
  heading: 'Documents',
  // ── EC-5, the on-demand states (spec §3.2) ──────────────────────────────────────────────────
  retrieving: 'Retrieving from Microsoft 365…',
  /** A source miss after a COMPLETED bounded search — not "we did not look". */
  sourceGone: 'No longer available from Microsoft 365',
  /** A temporary problem. The delay is the SERVER's; the retry is a human's. */
  retryLater: 'Temporarily unavailable',
  retry: 'Retry',
  /** The 30 s interactive budget lapsed. The server may still be working; this tab stopped. */
  stillWorking: 'Still checking — this may take a moment',
  refresh: 'Refresh',
  /** A file the manifest knows about but has no bytes for (acceptance R4). */
  retrieve: 'Retrieve',
} as const;

/** Per-item note under the name, or null when the row needs none (a ready file speaks for itself). */
function itemNote(item: CrmEmailAttachmentItem, busy: boolean): string | null {
  if (busy) return ATTACHMENT_COPY.retrieving;
  switch (item.state) {
    case 'blocked': return ATTACHMENT_COPY.blocked;
    case 'scan_pending': return ATTACHMENT_COPY.scanPending;
    case 'fetching': return ATTACHMENT_COPY.retrieving;
    case 'retry_later': return ATTACHMENT_COPY.retryLater;
    // ⛔ `unavailable` on a FILE means the bytes are gone from every eligible copy, or the
    // reference has no object. It is a completed answer, not "we have not looked" — which is
    // `not_stored`, and reads differently on purpose (spec §3.2).
    case 'unavailable': return ATTACHMENT_COPY.sourceGone;
    case 'not_stored': return item.can_fetch ? null : ATTACHMENT_COPY.notStored;
    default: return null;
  }
}

export interface AttachmentListProps {
  /**
   * What the surface's own content query is doing right now, from
   * `attachmentListStateFromQuery`. It is a STATE, not a pair of booleans, because the case that
   * mattered — a refetch in flight over a previous success — is the one a boolean pair keeps
   * getting wrong.
   */
  state: AttachmentListState;
  /**
   * The status envelope for THIS open. The caller passes it ONLY when `state === 'ready'`; this
   * component additionally refuses to read it in any other state, so a stale envelope cannot paint
   * even if a caller forgets (review BLOCKER 1 — belt and braces, because what leaks here is
   * filenames).
   */
  content?: CrmEmailContent;
  /** View (PDF only). Omitted → the View control is not rendered at all. */
  onView?: (item: CrmEmailAttachmentItem) => void;
  onDownload?: (item: CrmEmailAttachmentItem) => void;
  /** The id currently being fetched, so its own control reads "Opening…" and cannot be double-fired. */
  busyId?: number | null;
  /** EC-5 — retrieve THIS file's bytes from Microsoft 365 (R4), or retry a temporary failure. */
  onRetrieve?: (item: CrmEmailAttachmentItem) => void;
  /** Item ids with an acquisition in flight, so their row reads "Retrieving…" and cannot be re-fired. */
  pendingIds?: Set<number>;
  /** True while the MANIFEST itself is being enumerated — the empty list says so rather than
   *  claiming there are no documents. */
  manifestBusy?: boolean;
  /** The 30 s waiting budget has been spent: polling has stopped, so the list offers a manual
   *  Refresh instead of a spinner that resolves for nobody. */
  budgetSpent?: boolean;
  onRefresh?: () => void;
  className?: string;
}

export function AttachmentList({
  state: queryState, content, onView, onDownload, busyId, onRetrieve, pendingIds, manifestBusy,
  budgetSpent, onRefresh, className,
}: AttachmentListProps) {
  // ⛔ LOADING FIRST, AND IT RENDERS NO NAMES. `loading` covers the first fetch AND every refetch,
  // including the one that is about to return 403 — which is the whole point (review BLOCKER 1).
  if (queryState === 'loading') {
    return (
      <div className={className} data-attachment-list="loading">
        <div className={styles.note} role="status">{ATTACHMENT_COPY.loading}</div>
      </div>
    );
  }
  if (queryState === 'error') {
    return (
      <div className={className} data-attachment-list="error">
        <div className={styles.error} role="alert">{ATTACHMENT_COPY.error}</div>
      </div>
    );
  }
  // 403 (not on the thread) and 404 (flag off / not visible) render NOTHING: the body card above
  // already tells the entitlement story, and a second "Failed to load" under it would turn the
  // feature flag into a visible defect. Separate branches so neither can be widened by accident.
  if (queryState === 'forbidden' || queryState === 'missing') return null;
  // `ready` is the only state that may read the envelope. The `attachments` guard is the same
  // answer for a response that is not the envelope at all (an older backend, a stub): a list that
  // cannot be read is a list that is not shown, never a crashed row.
  if (!content?.attachments) return null;

  const { state, items = [] } = content.attachments;
  if (items.length === 0) {
    // ⛔ THESE THREE SENTENCES ARE DIFFERENT FACTS AND MUST STAY DIFFERENT WORDS (spec §3.2):
    // `empty` = we know there are none; `fetching` = we are looking right now; `not_captured` =
    // nobody has looked. Collapsing them is how a reader concludes a deal has no documents.
    const words = state === 'empty' ? ATTACHMENT_COPY.empty
      : (manifestBusy || state === 'fetching') ? ATTACHMENT_COPY.retrieving
        : ATTACHMENT_COPY.notCaptured;
    return (
      <div className={className} data-attachment-list={manifestBusy ? 'fetching' : state}>
        <div className={styles.note} role={manifestBusy ? 'status' : undefined}>{words}</div>
        {budgetSpent && onRefresh && (
          <div className={styles.note} role="status">
            {ATTACHMENT_COPY.stillWorking}
            <button type="button" className={styles.action} onClick={onRefresh}>
              {ATTACHMENT_COPY.refresh}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={className} data-attachment-list={state}>
      <div className={styles.head}>
        {ATTACHMENT_COPY.heading}
        {/* Partial is stated, never hidden: a list that is still being enumerated must not read as
            the complete set (spec §7.2 — "Partial lists are never 'no attachments'"). */}
        {!content.attachments.complete && (
          <span className={styles.partial}>{ATTACHMENT_COPY.partial}</span>
        )}
      </div>
      {/* ⛔ The budget STOPPED the polling, so the only honest thing on screen is a control that
          asks again. A surface that keeps spinning is claiming to be waiting when nothing is
          watching — which is how "scan_pending on every file" reads as a hung product rather than
          a permission that needs granting (review MAJOR 2). */}
      {budgetSpent && onRefresh && (
        <div className={styles.note} role="status">
          {ATTACHMENT_COPY.stillWorking}
          <button type="button" className={styles.action} onClick={onRefresh}>
            {ATTACHMENT_COPY.refresh}
          </button>
        </div>
      )}
      <ul className={styles.list}>
        {items.map((item) => {
          const busy = busyId === item.id || pendingIds?.has(item.id) === true;
          const note = itemNote(item, busy);
          return (
            <li key={item.id} className={styles.row} data-attachment-state={item.state}>
              <span className={styles.type}>{typeLabel(item.content_type)}</span>
              <span className={styles.name} title={item.name ?? undefined}>
                {item.name || '(unnamed file)'}
                {note && <span className={styles.itemNote}>{note}</span>}
              </span>
              <span className={styles.size}>{formatSize(item.size_bytes)}</span>
              <span className={styles.actions}>
                {onView && item.can_preview && (
                  <button
                    type="button" className={styles.action}
                    disabled={busy} onClick={() => onView(item)}
                  >
                    {busy ? 'Opening…' : 'View'}
                  </button>
                )}
                {onDownload && item.can_download && (
                  <button
                    type="button" className={styles.action}
                    disabled={busy} onClick={() => onDownload(item)}
                  >
                    {busy ? 'Opening…' : 'Download'}
                  </button>
                )}
                {/* R4 — a file the manifest knows about but has no bytes for. ONE click retrieves
                    THAT file; no sibling is prefetched, and the affordance appears only because
                    the SERVER said `can_fetch`. */}
                {onRetrieve && item.can_fetch && !item.can_download && (
                  <button
                    type="button" className={styles.action}
                    disabled={busy} onClick={() => onRetrieve(item)}
                  >
                    {busy ? ATTACHMENT_COPY.retrieving : ATTACHMENT_COPY.retrieve}
                  </button>
                )}
                {/* A TEMPORARY problem gets an explicit, human-triggered retry with the server's
                    own delay — never an automatic one (spec §3.2: "no automatic retry storm").
                    A 403 never reaches here: it is not a state, it is a refusal, and the list
                    renders nothing at all for it. */}
                {onRetrieve && item.state === 'retry_later' && (
                  <button
                    type="button" className={styles.action}
                    disabled={busy} onClick={() => onRetrieve(item)}
                  >
                    {ATTACHMENT_COPY.retry}
                    {item.retry_after_seconds ? ` (${item.retry_after_seconds}s)` : ''}
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default AttachmentList;
